/**
 * Entry point for production client
 *
 * Register included plugins here and initialize Controller.
 *
 * In the self-executing "main" function, depending on context initialize in various modes:
 *
 * [![main.js flowchart](https://www.websequencediagrams.com/cgi-bin/cdraw?lz=dGl0bGUgQ2xpZW50IG1haW4uanMKCnBhcnRpY2lwYW50ABUHCiAgCmFsdCBFZGl0b3IgbW9kZSByZXF1ZXN0ZWQKICBRdWVyeSBwYXJhbSA_b3B0aW1pemVseV9wMTNuIGlzIHByZXNlbnQuCiAAZgctPkFwcDogbG9hZCBfX0lOTklFX0pTX1VSSV9fCiAgQXBwABYMcDEzbl9pbm5lcgAUCCsAgQQGADwGCiAAgRIHAEoMAHsLZQCBMQVfZGF0YV9lbmRwb2kAgU0FcGFyYWxsZWwgewogIABtBwBPCAAtBiBkYXRhABgFICBJbml0aWFsaXplIHdpdGggdGhlABoMIHJldHJpZXYAggoFICBub3RlIHJpZ2h0IG9mAIIwBzogaQA7CQogIH0KICBkZXN0cm95AIJRBwplbHNlIFByZXZpZXcAgjsqdG9rZQCCWgwgb3IgY29va2llAIF6C1QAFg8uCgCBFRAAhAAGOiBzZXR1cCBwAIB_B2dsb2JhbACDLQsrAIEZBwCDNAlQUkVWSUVXX0NMSUVOVF9FTkRQT0lOVACDQwUAgUUHAINgDgApCERBVEEAIQ4Agn4UAGYJAIIQCACCdR8AgTEIAIJ0IQCBPwkAgn0ZAIMDBwCDEQZPdGhlcndpc2UAhCgFAIN-EGJ1aWx0LWluIERBVEEAgkIZAIN1C2VuZAoK&s=modern-blue&h=x4uMUzJL8g9gyLfX)](https://www.websequencediagrams.com/#open=683091)
 *
 * If currently in normal mode:
 *
 *   1. If editor requested (?optimizely_p13n is present): load innie.js
 *   2. Else if preview mode requested (?optimizely_token is present or cookie optimizelyToken is present):
 *
 *       1. Setup preview global
 *       2. Load preview snippet
 *
 *   3. Else initialize normally
 *
 * If currently in preview mode:
 *
 *   1. Setup callback for preview data which calls initialize
 *   2. Load preview data
 *
 * If currently in editor mode:
 *
 *   1. Setup callback for edtior data which calls initialize
 *   2. Load editor data
 *
 * In each case "initialize" loads all configured plugins, then takes a given ClientData and calls [Controller.initialize](#module_controller.initialize) on it.
 *
 * @module
 */

function main() {
    // Create the start mark before loading performance module, in order to capture webpack overhead
  if (window.performance && window.performance.mark) {
    window.performance.mark('optimizely:blockBegin');
  }

  var RUM = require('./rum');
  RUM.initialize();

  // Instrument some timeline events
  var perf = require('core/env/performance');
  perf.time('gtagReady'); // Time to GA4 function ready
  if (__PROFILE__ || __RUM_VERBOSE__) {
    perf.time('odpTime'); // Time to ODP request sent (representative of async providers)
    perf.time('sendDCP');  // Time to DCP request sent (representative of async providers)
    perf.time('sendActivate'); // Time to call first ClientActivation event trackers
    perf.time('sendViewActivate'); // Time to call first ViewActivated event trackers
    perf.time('sendDecision'); // Time to call first Decision event trackers
    perf.time('flash');  // Time to first Action.executePreparedAction
    perf.time('render');
    perf.time('preInitialize');

    if (__CMAB_IS_ENABLED__) perf.time('sendCmabApiInitiateCall'); // Time to initiate CMAB API call

    // Callback to be used for each of the Core Web Vitals
    // When a value is reported for the metric
    function webVitalsCb({name, delta, value, id}) {
      if (name === 'CLS') {
        // CLS is measured in percentages betweeb 0.0 and 1.0
        // Multiplying by 100 allows for converting to int without losing meaning
        // https://web.dev/cls
        value = value * 100;
      }

      perf.setMark(name, perf.now(), value);
    }

    var webVitals = require('web-vitals');
    // FCP == First Contentful Paint. https://web.dev/fcp/
    webVitals.onFCP(webVitalsCb);
    // FID == First Input Delay. https://web.dev/fid/
    webVitals.onFID(webVitalsCb);
    // TTFB == Time to First Byte. https://web.dev/ttfb/
    webVitals.onTTFB(webVitalsCb);

    // We report on all changes for LCP, CLS, and INP, because they
    // either report after a user event (like click) or later,
    // past the point at which we report our RUM data.
    // Reporting on all changes ensures we get at least some data when it's available.
    // However, sometimes (especially with CLS) this results in multiple values reported.
    // Note: client-rum aggregate these values as Average
    // (https://github.com/optimizely/client-rum/blob/master/src/backends/honeycomb/index.js#L138),
    // but Maximum (ie. total CLS) would be a better aggregator in some cases.
    // LCP == Largest Contentful Paint. https://web.dev/lcp/
    webVitals.onLCP(webVitalsCb, {reportAllChanges: true});
    // CLS == Cumulative Layout Shift. https://web.dev/cls/
    webVitals.onCLS(webVitalsCb, {reportAllChanges: true});
    // INP == Interactino to Next Paint. https://web.dev/inp/
    webVitals.onINP(webVitalsCb, {reportAllChanges: true});
  }

  if (__POLYFILL_ES5__) {
    require('es5-shim');
  }

  var Logger = require('core/utils/logger');

  // Detect support for CORS (required)
  var di = require('core/di');
  // Register env modules for di
  require('./env');

  var DirectiveStore = di.get('stores/directive');
  var XHR = require('core/utils/xhr');
  if (!XHR.isCORSSupported()) {
    throw new Error('CORS is not supported on this browser, aborting.');
  }

  var Cookie = require('core/utils/cookie');
  var dataApi = require('./api/data');
  var Directive = require('core/directive');
  var scriptUtils = require('core/utils/script');

  var DATA = __DATA__;

  var Controller = require('./controller');

  // TODO: Check if this is the right place
  if (__EXPERIMENTAL_WEB_SDK__){
    const { processWebSdk: WebSdkProcessor } = require('./web_sdk');
    WebSdkProcessor(__WEB_SDK_DATA__);
  }

  var dataEndpoint;
  var initPreviewFnName = 'initializeOptimizelyPreview';


  // Initialize global store first so error_monitor has appropriate
  // accountId/projectId values in case of error
  Controller.initGlobalStore(DATA);

  Directive.populateDirectiveData();

  if (DirectiveStore.clientHasAlreadyInitialized()) {
    Logger.warn('Main / Disabling because Optimizely has already initialized on this page load. ' +
                'Are there multiple snippets on the page?');
    return;
  }

  // Client should do nothing if in the desktop app for a non __EDITOR__ build.
  if (DirectiveStore.shouldBailForDesktopApp()) {
    Logger.log('Main / Disabling because of desktop app.');
    return;
  }

  // If client is told to observe changes indefinitely but the browser doesn't support it, bail.
  if (DirectiveStore.conflictInObservingChanges()) {
    Logger.log('Main / Disabling: Observe Changes Indefinitely is on, but browser does not support it.');
    return;
  }
  if (DirectiveStore.shouldLoadInnie()) {
    // Allow the editor to determine which project ID was actually instrumented on the page.
    // TODO(CJS-3534) Unregister this function once logic to use getAccountId is switched over
    // and verified in prod.
    dataApi.registerFunction('getProjectId', function () {
      return DATA.projectId;
    });
    // Allow the editor to determine which account ID was actually instrumented on the page.
    dataApi.registerFunction('getAccountId', function () {
      return DATA.accountId;
    });
    scriptUtils.addScriptAsync(__INNIE_JS_URI__);
    Logger.log('Main / Disabling in favor of the editor client.');
  } else if (DirectiveStore.shouldLoadEmbeddedEditor()) {
    Logger.log('Main / Loading embedded editor');
    // eslint-disable-next-line lodash/prefer-lodash-method
    const useLocalBundle = Cookie.get('useLocalBundle') === 'true';
    // Make sure the editor is able to instantiate
    Directive.setOptOut(false);
    const MfeEntrypoint = useLocalBundle ? 'http://localhost:9888/main.js' : __VISUAL_EDITOR_MFE_URL__;
    scriptUtils.addScriptAsync(MfeEntrypoint);
  } else if (DirectiveStore.shouldLoadPreview()) {
    // Push liveCommit data into the global before fetching the preview client.
    if (__GET_ONLY_PREVIEW_LAYERS__) {
      // If enabled, send the liveCommit data from prod client to preview client.
      // The preview client will do an additional fetch only for data not included in the liveCommit for layers being previewed.
      var relevantGlobal;

      if (DirectiveStore.isSlave()) {
        // In bundled mode, __GLOBAL__ will refer to SLAVE_CLIENT.optimizely instead of the window.optimizely global,
        // which cannot be read from the preview bundle, causing the ensuing 'load' push call to never be executed.
        // Hardcode a reference to window.optimizely in this case, which in slave mode is guaranteed to be defined.
        relevantGlobal = window.optimizely;
      } else {
        relevantGlobal = __GLOBAL__ = __GLOBAL__ || [];
      }

      // Load the liveCommit data (add'l preview data loaded in shouldBootstrapDataForPreview)
      relevantGlobal.push({type: 'load', data: DATA});
    }
    Logger.log('Main / Disabling in favor of the preview client.');

    // Send the liveCommit data to preview_ui, as preview client may be initialized with some working copy data.
    // NB: Do not move this below the addScriptSync call. We must define the "optimizely" and "optimizelyPreview"
    // globals before loading any scripts which may assume they exist (see below)...
    //
    // Background: We use two methods to load an external script in `scriptUtils.addScriptSync`. The order
    // in which the code runs depends on how it's loaded:
    //
    // 1) document.write --> default
    //      When preview client is added by the client via document.write, preview client's code will not execute
    //      until the client's synchronous code has finished running.
    //
    // 2) sync xhr.request --> fallback, used when the client is loaded async
    //      If document.write fails for any reason, such as when client is loaded async, then we fall back to
    //      sync xhr.request. In this case, preview client will execute inline before the rest of client.
    //      Therefore, we must define any dependencies that preview client uses before we load it.

    require('./preview_setup').setupPreviewGlobal();
    require('./preview_setup').pushToPreviewGlobal({
      type: 'pushPreviewData',
      name: 'liveCommitData',
      data: DATA,
    });

    // In bundled mode, the legacy client has already bootstrapped the preview client for us.
    if (!DirectiveStore.isSlave()) {
      // Allow the preview client to determine which project ID was actually instrumented on the page.
      dataApi.registerFunction('getProjectId', function() {
        return DATA.projectId;
      });
      scriptUtils.addScriptSync(__PREVIEW_CLIENT_ENDPOINT__);
    }

  } else if (DirectiveStore.shouldBootstrapDataForPreview()) {
    // Bootstrap data for preview
    dataApi.registerFunction(initPreviewFnName, function initializePreview(clientData:any) {
      initialize(clientData);
      dataApi.unregisterFunction(initPreviewFnName);
    });
    var projectId = DirectiveStore.isSlave() ? PROJECT_ID_FOR_SLAVE_PREVIEW : dataApi.getFunction('getProjectId')();
    dataEndpoint = getDataEndpointUrl(DirectiveStore.getProjectToken(), projectId, DirectiveStore.getPreviewLayerIds());
    var forceVariationIds = DirectiveStore.getForceVariationIds();
    if (forceVariationIds && forceVariationIds.length > 0) {
      dataEndpoint += '&optimizely_x=' + forceVariationIds.join(',');
    }
    // Load preview data synchronously to mimic production client timing of code execution.
    scriptUtils.addScriptSync(dataEndpoint);
    // Load preview UI script asynchronously as it is large and is safe to load at anytime.
    // Todo(james): Remove this if condition once the build process has been updated to provide this endpoint.
    if (__PREVIEW_UI_ENDPOINT__) {
      require('./preview_setup').setupPreviewGlobal();
      scriptUtils.addScriptAsync(__PREVIEW_UI_ENDPOINT__);
    }

  } else if (DirectiveStore.shouldBootstrapDataForEditor()) {
    // Bootstrap data in editor mode -- add async to avoid rewriter issues
    dataApi.registerFunction(initPreviewFnName, function initializeEditor(clientData) {
      initialize(clientData);
      dataApi.unregisterFunction(initPreviewFnName);
    });
    scriptUtils.addScriptAsync(window.optimizely_editor_data_endpoint);
  } else if (DirectiveStore.shouldInitialize()) {
    // If we are in slave mode on initial load and the user has specified a token to bootstrap the preview client,
    // do nothing. The old client will do all of the bootstrapping for us.
    initialize(DATA);
  }

  /**
   * Run client: load configured plugins, then call Controller.initialize
   * @param {object} clientData All project specific data
   */
  function initialize(clientData) {
    if (__PREVIEW__) {
      require('./preview_setup').initialize(clientData.projectId);

      // This prevents Project JS from running twice in bundled client
      if (DirectiveStore.isSlave()) {
        clientData.projectJS = null;
      }
    }

    // Always include visitor_id plugin
    var VisitorIdPlugin = require('./plugins/feature_visitor_id');
    var plugins = [VisitorIdPlugin];

    // Features
    if (__TARGETING_CONDITIONS__.PREDICTED_INTENT) {
      plugins.push(require('./plugins/feature_behavior/adaptive_audiences'));
    }
    if (__TARGETING_CONDITIONS__.BEHAVIOR ||
      __TARGETING_CONDITIONS__.FIRST_SESSION ||
      __SEGMENTATION_FEATURES__.RETURNING) {
      plugins.push(require('./plugins/feature_behavior/behavior_events'));
      if(__SEGMENTATION_FEATURES__.CUSTOM_BEHAVIOR || __TARGETING_CONDITIONS__.BEHAVIOR) {
        plugins.push(require('./plugins/feature_behavior/custom_behavior'));
      }
      if(__SEGMENTATION_FEATURES__.FIRST_SESSION ||
        __TARGETING_CONDITIONS__.FIRST_SESSION ||
        __SEGMENTATION_FEATURES__.RETURNING) {
        plugins.push(require('./plugins/feature_behavior/first_session'));
      }
      if(__API_PLUGIN__.BEHAVIOR) {
        plugins.push(require('./plugins/feature_behavior/api'));
      }
    }
    if (__TARGETING_CONDITIONS__.BROWSER_VERSION ||
        // TODO(tyler): slimmer library for only segmentation
      __SEGMENTATION_FEATURES__.BROWSER_ID) {
      plugins.push(require('./plugins/feature_browser_version'));
    }
    if (__TARGETING_CONDITIONS__.CAMPAIGN ||
      __SEGMENTATION_FEATURES__.CAMPAIGN) {
      plugins.push(require('./plugins/feature_campaign'));
    }
    if (__TARGETING_CONDITIONS__.CODE) {
      plugins.push(require('./plugins/feature_code'));
    }
    if (__TARGETING_CONDITIONS__.COOKIES) {
      plugins.push(require('./plugins/feature_cookies'));
    }
    if (__TARGETING_CONDITIONS__.CUSTOM_ATTRIBUTE ||
        __TARGETING_CONDITIONS__.CUSTOM_DIMENSION ||
        __SEGMENTATION_FEATURES__.CUSTOM_ATTRIBUTE) {
      plugins.push(require('./plugins/feature_custom_attribute'));
    }
    if (__TARGETING_CONDITIONS__.DYNAMIC_CUSTOMER_PROFILE) {
      plugins.push(require('./plugins/feature_dcp'));
    }
    if (__TARGETING_CONDITIONS__.DEVICE ||
        // TODO(tyler): slimmer library for only segmentation
      __SEGMENTATION_FEATURES__.DEVICE) {
      plugins.push(require('./plugins/feature_device'));
    }
    if (__TARGETING_CONDITIONS__.DEVICE_TYPE ||
      __SEGMENTATION_FEATURES__.DEVICE_TYPE) {
      plugins.push(require('./plugins/feature_device_type'));
    }
    if (__TARGETING_CONDITIONS__.IP) {
      plugins.push(require('./plugins/feature_ip'));
    }
    if (__TARGETING_CONDITIONS__.LANGUAGE) {
      plugins.push(require('./plugins/feature_language'));
    }
    // `plugins/feature_list` is needed by the uploaded audiences (aka "list targeting") feature as well as server-side
    // audience integrations like Marketo.
    // TODO(jon): Instead make use of a map of features to other features that depend on them?
    if (__TARGETING_CONDITIONS__.LIST || __TARGETING_CONDITIONS__.THIRD_PARTY_MARKETO) {
      plugins.push(require('./plugins/feature_list'));
    }
    if (__TARGETING_CONDITIONS__.LOCATION ||
      __SEGMENTATION_FEATURES__.LOCATION) {
      plugins.push(require('./plugins/feature_location'));
    }
    if (__TARGETING_CONDITIONS__.PLATFORM) {
      plugins.push(require('./plugins/feature_platform'));
    }
    if (__TARGETING_CONDITIONS__.QUERY) {
      plugins.push(require('./plugins/feature_query'));
    }
    if (__TARGETING_CONDITIONS__.REFERRER ||
      __SEGMENTATION_FEATURES__.REFERRER) {
      plugins.push(require('./plugins/feature_referrer'));
    }
    if (__TARGETING_CONDITIONS__.SOURCE_TYPE ||
      __SEGMENTATION_FEATURES__.SOURCE_TYPE) {
      plugins.push(require('./plugins/feature_source_type'));
    }
    if (__TARGETING_CONDITIONS__.TIME_AND_DAY ||
      __SEGMENTATION_FEATURES__.CURRENT_DATE) {
      plugins.push(require('./plugins/feature_time_and_day'));
    }

    // Third party
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_LOTAME) {
      plugins.push(require('./plugins/feature_lotame'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_ADOBE_AUDIENCE_MANAGER) {
      plugins.push(require('./plugins/feature_adobe_audience_manager'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_NEW_ADOBE_AUDIENCE_MANAGER) {
      plugins.push(require('./plugins/feature_new_adobe_audience_manager'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_BLUEKAI) {
      plugins.push(require('./plugins/feature_bluekai'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_DEMANDBASE) {
      plugins.push(require('./plugins/feature_demandbase'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_GOOGLE_ADWORDS) {
      plugins.push(require('./plugins/feature_google_adwords'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_FACEBOOK_ADS) {
      plugins.push(require('./plugins/feature_facebook_ads'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_KRUX) {
      plugins.push(require('./plugins/feature_krux'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_LYTICS) {
      plugins.push(require('./plugins/feature_lytics'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_MARKETO) {
      plugins.push(require('./plugins/feature_marketo'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_SKYMOSITY) {
      plugins.push(require('./plugins/feature_skymosity'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_TEALIUM) {
      plugins.push(require('./plugins/feature_tealium'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_ODP) {
      plugins.push(require('./plugins/feature_odp'));
    }
    if (__TARGETING_CONDITIONS__.THIRD_PARTY_GOOGLE_ANALYTICS_4_AUDIENCE_TARGETING) {
      plugins.push(require('./plugins/feature_google_analytics_4_audience_targeting'));
    }

    // Analytics
    if (__ANALYTICS__.ADOBE_ANALYTICS) {
      plugins.push(require('./plugins/tracker_adobe_analytics'));
    }
    if (__ANALYTICS__.CLICKTALE) {
      plugins.push(require('./plugins/tracker_clicktale'));
    }
    if (__ANALYTICS__.GOOGLE_UNIVERSAL_ANALYTICS) {
      plugins.push(require('./plugins/tracker_google_universal_analytics'));
    }
    if (__ANALYTICS__.GOOGLE_CLASSIC_ANALYTICS) {
      plugins.push(require('./plugins/tracker_google_classic_analytics'));
    }
    if (__ANALYTICS__.GOOGLE_ANALYTICS_4) {
      plugins.push(require('./plugins/tracker_google_analytics_4'));
    }
    if (__ANALYTICS__.MIXPANEL) {
      plugins.push(require('./plugins/tracker_mixpanel'));
    }
    if (__ANALYTICS__.OPTIMIZELY) {
      plugins.push(require('./plugins/tracker_optimizely'));
    }

    // View Features
    if (__VIEW_FEATURES__.URL) {
      plugins.push(require('./plugins/view_feature_url'));
    }
    if (__VIEW_FEATURES__.CUSTOM_CODE) {
      plugins.push(require('./plugins/view_feature_custom_code'));
    }
    if (__VIEW_FEATURES__.ELEMENT_PRESENT) {
      plugins.push(require('./plugins/view_feature_element_present'));
    }

    // View Triggers
    if (__VIEW_TRIGGERS__.DOM_CHANGED) {
      plugins.push(require('./plugins/view_trigger_domchanged'));
    }
    if (__VIEW_TRIGGERS__.URL_CHANGED) {
      plugins.push(require('./plugins/view_trigger_urlchanged'));
    }

    // View Tag Features
    if (__VIEW_TAG_FEATURES__.CSS_SELECTOR) {
      plugins.push(require('./plugins/view_tag_feature_selector'));
    }

    if (__VIEW_TAG_FEATURES__.JAVASCRIPT) {
      plugins.push(require('./plugins/view_tag_feature_javascript'));
    }

    // Event Types
    if (__EVENT_TYPES__.CLICK) {
      plugins.push(require('./plugins/event_type_click'));
    }

    // Recommender API.
    if (__RECOMMENDER__) {
      plugins.push(require('./plugins/feature_recommender'));
    }

    if (__CHANGE__.APPEND) {
      plugins.push(require('./plugins/change_append'));
    }
    if (__CHANGE__.ATTRIBUTE) {
      plugins.push(require('./plugins/change_attribute'));
    }
    if (__CHANGE__.CUSTOM_CODE) {
      plugins.push(require('./plugins/change_custom_code'));
    }
    if (__CHANGE__.REARRANGE) {
      plugins.push(require('./plugins/change_rearrange'));
    }
    if (__CHANGE__.REDIRECT) {
      plugins.push(require('./plugins/change_redirect'));
    }
    if (__CHANGE__.WIDGET) {
      plugins.push(require('./plugins/change_widget'));
    }

    if (__POLICY__.ORDERED) {
      plugins.push(require('./plugins/decider_ordered'));
    }
    if (__POLICY__.RANDOM) {
      plugins.push(require('./plugins/decider_random'));
    }
    if (__POLICY__.EQUAL_PRIORITY) {
      plugins.push(require('./plugins/decider_equal_priority'));
    }
    if (__POLICY__.SINGLE_EXPERIMENT || __POLICY__.MULTIVARIATE) {
      plugins.push(require('./plugins/decider_single_experiment'));
    }

    Controller.initializePlugins(plugins);
  }

  /**
   * Construct the data endpoint url
   * @param {string} token
   * @param {string} _projectId
   * @param {Array.<string>} previewLayerIds
   * @returns {string} endpoint url
   */
  function getDataEndpointUrl(token:string, _projectId, previewLayerIds:string[]): string {
    return __PREVIEW_DATA_ENDPOINT__
             .replace('__TOKEN__', token)
             .replace('__PROJECT_ID__', _projectId)
             .replace('__PREVIEW_LAYER_IDS__', previewLayerIds.join(','))
             .replace('__GET_ONLY_PREVIEW_LAYERS__', __GET_ONLY_PREVIEW_LAYERS__);
  }

  perf.timeEnd('block');
  var now:number = perf.now();
  perf.setMark('optimizelyFinished', now, now);
}

if (__INVARIANT__) {
  main();
} else {
  try {
    main();
  }
  catch (e) {
    // Failsafe for uncaught errors in main.js.
    try {
      // Maybe we can log it
      require('./error_monitor').handleError(e);
    }
    catch (err) {
      // oh well, we tried
      console.log(err); // eslint-disable-line no-console
    }
  }
}
